import { getPlatform } from '@shared/lib/mixpanel/platformType';
import { getCookie } from '@shared/util/cookie';
import { getConfigString } from '@shared/util/env';
import { DEV } from 'esm-env';
import {
  MutableRefObject,
  ReactNode,
  createContext,
  memo,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
} from 'react';
import mixpanel from './mixpanel';

type BaseFlowData = Record<string, unknown>;
type MixpanelFlows = Record<string, BaseFlowData>;
type UpdateParam<T> = Partial<T> | ((previous: T) => T);

type FlowActions<TFlowName, TFlowData> = {
  commitFlow: (name: TFlowName, dataOrUpdater?: UpdateParam<TFlowData>) => void;
  getFlow: (name: TFlowName) => TFlowData | undefined;
  registerFlow: (name: TFlowName, init: TFlowData) => () => void;
  updateFlow: (name: TFlowName, dataOrUpdater: UpdateParam<TFlowData>) => TFlowData | undefined;
};

const createActions = <
  TFlowName extends string = string,
  TFlowData extends BaseFlowData = BaseFlowData,
>(
  flowsRef: MutableRefObject<Partial<MixpanelFlows>>,
): FlowActions<TFlowName, TFlowData> => {
  const getFlow = (flow: TFlowName) => flowsRef.current[flow] as TFlowData | undefined;

  /* eslint-disable no-param-reassign */
  const updateFlow = (flow: TFlowName, dataOrUpdater: UpdateParam<TFlowData>) => {
    const flowData = getFlow(flow);
    if (flowData) {
      const newData =
        typeof dataOrUpdater === 'function'
          ? dataOrUpdater(flowData)
          : { ...flowData, ...dataOrUpdater };
      flowsRef.current[flow] = newData;
      return newData;
    }
  };
  const commitFlow = (flow: TFlowName, dataOrUpdater?: UpdateParam<TFlowData>) => {
    let data = getFlow(flow);
    if (!data) {
      return;
    }
    if (dataOrUpdater) {
      data = updateFlow(flow, dataOrUpdater);
    }
    if (!data) {
      return;
    }

    mixpanel.track(flow, data);
    delete flowsRef.current[flow];
  };

  const registerFlow = (flow: TFlowName, init: TFlowData) => {
    flowsRef.current[flow] = init;
    mixpanel.time_event(flow);

    return () => commitFlow(flow);
  };
  /* eslint-enable no-param-reassign */
  return { registerFlow, getFlow, updateFlow, commitFlow };
};
export const MixpanelContext = createContext(null as unknown as ReturnType<typeof createActions>);

export const useMixpanel = <TFlowName, TFlowData>() =>
  useContext(MixpanelContext) as unknown as FlowActions<TFlowName, TFlowData>;

const reportEmailReferral = (isLite: boolean) => {
  const prefix = 'mp_source_email';
  const params = new URLSearchParams(window.location.search);
  const emailParams: Record<string, string> = {};
  params.forEach((value, key) => {
    if (key.startsWith(prefix)) {
      const param = key === prefix ? 'email_name' : key.replace(`${prefix}_`, '');
      emailParams[param] = value;
    }
  });
  if (emailParams.email_name) {
    mixpanel.track('email_link_opened', { ...emailParams, platform: getPlatform(isLite) });
  }
};

type Props = {
  config?: Parameters<typeof mixpanel.init>[1];
  isLite?: boolean;
  children: ReactNode;
};
const MixpanelProvider = ({ config, children, isLite = false }: Props) => {
  const flowsRef = useRef<Partial<MixpanelFlows>>({});

  useEffect(() => {
    const cookie = getCookie('cookiePreferences');
    if (!cookie?.analytics_cookies) {
      mixpanel.disable('cookie');
    }
  }, []);

  useLayoutEffect(() => {
    const mixpanelProjectId = getConfigString('mixpanel-project-id');
    const initConfig = {
      api_host: getConfigString('mixpanel-api-host'),
      ...config,
    };
    /* istanbul ignore if */
    if (mixpanelProjectId) {
      if (DEV) {
        mixpanel.init(mixpanelProjectId, { ...initConfig, debug: true });
      } else {
        mixpanel.init(mixpanelProjectId, initConfig);
      }
    } else {
      mixpanel.init('test environment', initConfig);
      mixpanel.disable('test');
    }
    if (localStorage.getItem('mp_dry_run')) {
      mixpanel.disable('test');
    }
    reportEmailReferral(isLite);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const actions = useMemo(() => createActions(flowsRef), []);

  return <MixpanelContext.Provider value={actions}>{children}</MixpanelContext.Provider>;
};

// since this may get passed an object as prop, which might cause it to rerender every time,
// we memoize it for good, no need for it to ever rerender since the context value is stable
export default memo(MixpanelProvider, () => true);
